import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import FormikInputField from 'components/forms/fields/FormikInputField';
import Button from 'components/buttons/Button';
import axios from 'data/axios';
import toast from 'react-hot-toast';
import AutocompleteFormikField from 'components/forms/fields/AutocompleteFormikField';
import useUser from 'hooks/useUser';
import Area from 'models/Area';
import FormikCheckboxField from 'components/forms/fields/FormikCheckboxField';
import { setUser } from 'contexts/AppActions';
import mapUserProfile from 'models/mapping/mapUserProfile';
import AppContext from 'contexts/AppContext';
import InputField from 'components/forms/fields/InputField';

interface RegistrationFormState {
    first_name: string;
    last_name: string;
    area_id: number;
    newsletter: boolean;
}

const initialValues: RegistrationFormState = {
    first_name: '',
    last_name: '',
    area_id: null,
    newsletter: true
};

const schema = Yup.object().shape({
    first_name: Yup.string().required('Vnesi svoje ime'),
    last_name: Yup.string().required('Vnesi svoj priimek'),
    newsletter: Yup.boolean(),
    area_id: Yup.number().nullable()
});

const EditProfileForm = ({ onSuccess, municipalities }: { onSuccess: () => void; municipalities: Area[] }) => {
    const { dispatch } = useContext(AppContext);
    const { user } = useUser();
    const [initialFormState, setInitialFormState] = useState<RegistrationFormState>(initialValues);

    useEffect(() => {
        if (user) {
            setInitialFormState({
                first_name: user.first_name ?? '',
                last_name: user.last_name ?? '',
                area_id: user.area_id ?? null,
                newsletter: user.newsletter
            });
        }
    }, [user]);
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const response = await axios.post('/update-profile', values);
            const user = response.data?.data;
            setUser(dispatch, mapUserProfile(user));
            onSuccess();
            setSubmitting(false);
        } catch (e) {
            setSubmitting(false);
            toast.error('Prišlo je do napake. Osveži stran in poskusi znova.');
        }
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialFormState}
                validationSchema={schema}
                onSubmit={handleSubmit}
                validateOnBlur={true}
            >
                {({ isSubmitting, handleSubmit, setFieldValue, dirty }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 xl:grid-cols-2 max-w-2xl gap-4 xl:gap-8">
                            <div className="order-1">
                                <FormikInputField
                                    name="first_name"
                                    label="Ime"
                                    type={'text'}
                                    placeholder="Vnesi svoje ime"
                                    autocomplete="first_name"
                                />
                            </div>
                            <div className="order-3 xl:order-2">
                                <AutocompleteFormikField
                                    selectedOption={municipalities.find(
                                        (municipality) => municipality.id === initialFormState.area_id
                                    )}
                                    name="area_id"
                                    label="Občina prebivališča"
                                    tooltip="Podatek o tvoji občini nam pomaga pri prikazovanju tebi relevantnih idej za izlet."
                                    handleChange={(option) => setFieldValue('area_id', option?.value)}
                                    options={municipalities.map((municipality) => ({
                                        id: municipality.id,
                                        name: municipality.name,
                                        value: municipality.id
                                    }))}
                                />
                            </div>
                            <div className="order-2 xl:order-3">
                                <FormikInputField
                                    name="last_name"
                                    label="Priimek"
                                    type={'text'}
                                    placeholder="Vnesi svoj priimek"
                                    autocomplete="last_name"
                                />
                            </div>
                            <div className="order-4 mt-6">
                                <FormikCheckboxField
                                    name={'newsletter'}
                                    label={
                                        'Želim, da Kam za vikend na moj email občasno pošlje TOP ideje za izlet in me obvešča o novostih na portalu.'
                                    }
                                />
                            </div>
                            <div className="order-5">
                                <InputField
                                    value={user.email}
                                    name="email"
                                    label="Email"
                                    type={'text'}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="mt-8">
                            <Button
                                color={'secondary'}
                                variant={'contained'}
                                type={'submit'}
                                disabled={isSubmitting || !dirty}
                            >
                                <span className="font-bold">Shrani spremembe</span>
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default EditProfileForm;
